import React, { useState, useEffect } from 'react';
import { X, Upload, AlertCircle, FileText, Loader2 } from 'lucide-react';
import useEmployeeDocuments from '../../../hooks/useEmployeeDocuments';
import { useEmployees } from '../../../hooks/useEmployees';
import { CategoryIcon } from './components';

export default function DocumentForm({ document, employeeId, onClose, onSuccess }) {
  const { createDocument, updateDocument, loadCategorias, loadTipos, loading } = useEmployeeDocuments();
  const { employees, loading: loadingEmployees } = useEmployees('activo');

  const [categorias,    setCategorias]    = useState([]);
  const [tipos,         setTipos]         = useState([]);
  const [selectedFile,  setSelectedFile]  = useState(null);
  const [error,         setError]         = useState(null);
  const [formData, setFormData] = useState({
    employee_id:            employeeId || '',
    tipo_documento_codigo:  '',
    categoria_codigo:       '',
    nombre:                 '',
    descripcion:            '',
    numero_documento:       '',
    fecha_emision:          '',
    fecha_vencimiento:      '',
    nivel_confidencialidad: 'interno',
    requiere_firma:         false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCategoriasData();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (formData.categoria_codigo) loadTiposData(formData.categoria_codigo);
    else setTipos([]);
  }, [formData.categoria_codigo]); // eslint-disable-line

  useEffect(() => {
    if (document) {
      setFormData({
        employee_id:            document.employee_id,
        tipo_documento_codigo:  document.tipo_documento_codigo,
        categoria_codigo:       document.categoria_codigo,
        nombre:                 document.nombre || '',
        descripcion:            document.descripcion || '',
        numero_documento:       document.numero_documento || '',
        fecha_emision:          document.fecha_emision || '',
        fecha_vencimiento:      document.fecha_vencimiento || '',
        nivel_confidencialidad: document.nivel_confidencialidad || 'interno',
        requiere_firma:         document.requiere_firma || false,
      });
    }
  }, [document]);

  const loadCategoriasData = async () => {
    const result = await loadCategorias();
    if (result.success) setCategorias(result.categorias || []);
  };

  const loadTiposData = async (categoriaId) => {
    const result = await loadTipos(categoriaId);
    if (result.success) setTipos(result.tipos || []);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, archivo: 'El archivo no debe superar 10 MB' }));
      return;
    }
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      setErrors(prev => ({ ...prev, archivo: 'Solo PDF, DOC, DOCX, JPG o PNG' }));
      return;
    }
    setSelectedFile(file);
    setErrors(prev => ({ ...prev, archivo: null }));
  };

  const validate = () => {
    const e = {};
    if (!formData.employee_id)           e.employee_id           = 'Empleado requerido';
    if (!formData.categoria_codigo)      e.categoria_codigo      = 'Categoría requerida';
    if (!formData.tipo_documento_codigo) e.tipo_documento_codigo = 'Tipo requerido';
    if (!formData.nombre?.trim())        e.nombre                = 'Nombre requerido';
    if (!document && !selectedFile)      e.archivo               = 'Debe seleccionar un archivo';
    if (formData.fecha_vencimiento && formData.fecha_emision &&
        new Date(formData.fecha_vencimiento) < new Date(formData.fecha_emision)) {
      e.fecha_vencimiento = 'La fecha de vencimiento no puede ser anterior a la emisión';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    const result = document
      ? await updateDocument(document.id, formData)
      : await createDocument({ ...formData, archivo: selectedFile });

    if (result.success) {
      onSuccess?.();
      onClose();
    } else {
      setError(result.error || 'Error al guardar el documento');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText size={16} className="text-blue-600" />
            </div>
            <h2 className="text-base font-bold text-gray-900">
              {document ? 'Editar Documento' : 'Nuevo Documento'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Error global */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={14} className="flex-shrink-0" /> {error}
            </div>
          )}

          {/* Empleado */}
          {!employeeId && !document && (
            <Field label="Empleado" required error={errors.employee_id}>
              <select
                name="employee_id"
                value={formData.employee_id}
                onChange={handleChange}
                disabled={loadingEmployees}
                className={input(errors.employee_id)}
              >
                <option value="">Seleccionar empleado...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nombre} {emp.apellido} — {emp.employee_id}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {/* Categoría + Tipo (2 col) */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categoría" required error={errors.categoria_codigo}>
              <select
                name="categoria_codigo"
                value={formData.categoria_codigo}
                onChange={handleChange}
                disabled={!!document}
                className={input(errors.categoria_codigo, !!document)}
              >
                <option value="">Seleccionar...</option>
                {categorias.map(c => <option key={c.id} value={c.codigo}>{c.nombre}</option>)}
              </select>
            </Field>

            <Field label="Tipo de Documento" required error={errors.tipo_documento_codigo}>
              <select
                name="tipo_documento_codigo"
                value={formData.tipo_documento_codigo}
                onChange={handleChange}
                disabled={!formData.categoria_codigo || !!document}
                className={input(errors.tipo_documento_codigo, !formData.categoria_codigo || !!document)}
              >
                <option value="">Seleccionar...</option>
                {tipos.map(t => <option key={t.id} value={t.codigo}>{t.nombre}</option>)}
              </select>
            </Field>
          </div>

          {/* Nombre */}
          <Field label="Nombre del Documento" required error={errors.nombre}>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Ej: Cédula de Identidad — Juan Pérez"
              className={input(errors.nombre)}
            />
          </Field>

          {/* Descripción */}
          <Field label="Descripción">
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción adicional..."
              rows={2}
              className={input()}
            />
          </Field>

          {/* Número de documento */}
          <Field label="Número de Documento">
            {document ? (
              <input type="text" value={formData.numero_documento || 'N/A'} disabled className={input(null, true)} />
            ) : (
              <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-center gap-2">
                <FileText size={13} /> Se generará automáticamente (DOC-2026-XXXXXX)
              </div>
            )}
          </Field>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Fecha de Emisión">
              <input type="date" name="fecha_emision" value={formData.fecha_emision} onChange={handleChange} className={input()} />
            </Field>
            <Field label="Fecha de Vencimiento" error={errors.fecha_vencimiento}>
              <input type="date" name="fecha_vencimiento" value={formData.fecha_vencimiento} onChange={handleChange} className={input(errors.fecha_vencimiento)} />
            </Field>
          </div>

          {/* Confidencialidad + Requiere firma */}
          <div className="grid grid-cols-2 gap-4 items-start">
            <Field label="Nivel de Confidencialidad" required>
              <select name="nivel_confidencialidad" value={formData.nivel_confidencialidad} onChange={handleChange} className={input()}>
                <option value="publico">Público</option>
                <option value="interno">Interno</option>
                <option value="confidencial">Confidencial</option>
                <option value="secreto">Secreto</option>
              </select>
            </Field>

            <div className="pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="requiere_firma"
                  checked={formData.requiere_firma}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Requiere firma</span>
              </label>
            </div>
          </div>

          {/* Upload archivo */}
          {!document && (
            <Field label="Archivo" required error={errors.archivo}>
              <label
                htmlFor="file-upload"
                className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
                  errors.archivo ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <Upload size={24} className="text-gray-400" />
                <p className="text-sm font-medium text-gray-700">
                  {selectedFile ? selectedFile.name : 'Seleccionar archivo'}
                </p>
                {selectedFile ? (
                  <p className="text-xs text-gray-400">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                ) : (
                  <p className="text-xs text-gray-400">PDF, DOC, DOCX, JPG o PNG · máx. 10 MB</p>
                )}
                <input id="file-upload" type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" />
              </label>
            </Field>
          )}

          {/* Nota de edición */}
          {document && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              Para reemplazar el archivo, usa "Nueva Versión" en los detalles del documento.
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {document ? 'Guardar Cambios' : 'Crear Documento'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <p className="flex items-center gap-1 text-xs text-red-600 mt-1">
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

const input = (error, disabled) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-400 transition-colors ${
    error    ? 'border-red-400 bg-red-50'   :
    disabled ? 'border-gray-200 bg-gray-100 text-gray-500' :
               'border-gray-200 bg-white'
  }`;
